import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useProperty } from '@/lib/property/propertyContextDef';
import { useBookingsInRange, guestName, listingTitle } from '@/lib/data/bookings';
import { formatNad } from '@/lib/money/format';
import { computeBookingMoney } from '@/lib/money/bookingMoney';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/bookings/arrivals-print')({
  component: ArrivalsPrint,
});

/** Today's arrivals, no chrome — the header/nav are hidden by the global
 * `@media print` rule (src/styles/global.css) rather than this route
 * structurally lacking the shell, so the same `<header>` markup renders in
 * the browser (looks fine, harmless) and just never prints. */
function ArrivalsPrint() {
  const { businessId, memberships } = useProperty();
  const today = format(new Date(), 'yyyy-MM-dd');
  const { data: bookings, isLoading } = useBookingsInRange(businessId, today, today);
  const businessName = memberships.find((m) => m.businessId === businessId)?.businessName ?? '';
  const arrivals = (bookings ?? []).filter((b) => b.status !== 'cancelled' && b.status !== 'rejected');

  return (
    <div className="p-lg">
      <div className="mb-md flex items-center justify-between print:mb-sm">
        <div>
          <h1 className="text-h2 font-extrabold text-ink">Today's arrivals</h1>
          <p className="text-body text-ink-2">
            {businessName ? `${businessName} — ` : ''}
            {format(new Date(), 'EEEE d MMMM yyyy')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="print:hidden rounded-button bg-canyon px-md py-xs text-body font-semibold text-white"
        >
          Print
        </button>
      </div>

      {isLoading ? <PageLoading compact /> : null}

      {!isLoading && arrivals.length === 0 ? (
        <p className="text-body text-ink-2">No arrivals today.</p>
      ) : null}

      {!isLoading && arrivals.length > 0 ? (
        <table className="w-full border-collapse text-body text-ink">
          <thead>
            <tr className="border-b-2 border-ink text-left">
              <th className="py-xs pr-md">Reference</th>
              <th className="py-xs pr-md">Guest</th>
              <th className="py-xs pr-md">Listing</th>
              <th className="py-xs pr-md">Party</th>
              <th className="py-xs pr-md">Nights</th>
              <th className="py-xs">Balance due</th>
            </tr>
          </thead>
          <tbody>
            {arrivals.map((b) => {
              const money = computeBookingMoney({
                totalPrice: b.total_price,
                depositAmount: b.deposit_amount,
                balanceRecordedAmountNad: b.balance_recorded_amount_nad,
                feeStatus: b.fee_status,
                balanceStatus: b.balance_status,
              });
              return (
                <tr key={b.id} className="border-b border-hair">
                  <td className="py-xs pr-md font-semibold">{b.reference_number ?? b.id}</td>
                  <td className="py-xs pr-md">{guestName(b)}</td>
                  <td className="py-xs pr-md">{listingTitle(b)}</td>
                  <td className="py-xs pr-md">{b.guests ?? '—'}</td>
                  <td className="py-xs pr-md">
                    {b.check_in && b.check_out
                      ? Math.max(
                          1,
                          Math.round(
                            (new Date(b.check_out).getTime() - new Date(b.check_in).getTime()) / 86_400_000,
                          ),
                        )
                      : '—'}
                  </td>
                  <td className="py-xs">{formatNad(money.balanceDueCents / 100)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
